import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Bell, Calendar, Smile, Coffee, Trophy, Save, FolderDown, Sunrise, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export function NotificationSettings() {
    const { language } = useLanguage();
    const [preferences, setPreferences] = useState({
        weekly_report_enabled: true,
        weekly_report_day: 'sunday',
        weekly_report_time: '19:00',
        challenge_reminders: true,
        mood_reminders: true,
        morning_motivation: false,
        backup_reminder_enabled: true,
        morning_routine_hint_enabled: true,
        weekly_briefing_push_enabled: true,
        symptom_prediction_enabled: true,
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const t = {
        de: {
            title: 'Benachrichtigungen',
            description: 'Wann soll Nanouk dich erinnern?',
            weeklyReport: 'Wochenrückblick',
            weeklyReportDesc: 'Erhalte jeden Sonntag deinen Wochenrückblick',
            day: 'Tag',
            time: 'Uhrzeit',
            sunday: 'Sonntag',
            monday: 'Montag',
            challengeReminders: 'Challenge-Erinnerungen',
            challengeRemindersDesc: 'Meilenstein-Benachrichtigungen (Tag 1, 3, 5, 7)',
            moodReminders: 'Mood-Erinnerungen',
            moodRemindersDesc: 'Abendliche Erinnerung an den Mood-Check',
            morningMotivation: 'Morgen-Motivation',
            morningMotivationDesc: 'Starte den Tag mit einer Nachricht von Nanouk',
            save: 'Speichern',
            saved: 'Einstellungen gespeichert!'
        },
        en: {
            title: 'Notifications',
            description: 'When should Nanouk remind you?',
            weeklyReport: 'Weekly Report',
            weeklyReportDesc: 'Receive your weekly report every Sunday',
            day: 'Day',
            time: 'Time',
            sunday: 'Sunday',
            monday: 'Monday',
            challengeReminders: 'Challenge Reminders',
            challengeRemindersDesc: 'Milestone notifications (Day 1, 3, 5, 7)',
            moodReminders: 'Mood Reminders',
            moodRemindersDesc: 'Evening reminder for mood check',
            morningMotivation: 'Morning Motivation',
            morningMotivationDesc: 'Start the day with a message from Nanouk',
            save: 'Save',
            saved: 'Settings saved!'
        }
    }[language];

    useEffect(() => {
        loadPreferences();
    }, []);

    const loadPreferences = async () => {
        try {
            const response = await axios.get(`${API}/api/notifications/preferences`);
            setPreferences(prev => ({ ...prev, ...response.data }));
        } catch (error) {
            console.error('Failed to load preferences:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.post(`${API}/api/notifications/preferences`, preferences);
            toast.success(t.saved);
        } catch (error) {
            console.error('Failed to save preferences:', error);
            toast.error('Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    const updatePreference = (key, value) => {
        setPreferences(prev => ({ ...prev, [key]: value }));
    };

    if (loading) {
        return null;
    }

    return (
        <Card data-testid="notification-settings">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    {t.title}
                </CardTitle>
                <CardDescription>{t.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Weekly Report */}
                <div className="space-y-4 p-4 rounded-lg bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Calendar className="w-5 h-5 text-violet-500" />
                            <div>
                                <Label className="font-medium">{t.weeklyReport}</Label>
                                <p className="text-xs text-muted-foreground">{t.weeklyReportDesc}</p>
                            </div>
                        </div>
                        <Switch
                            checked={preferences.weekly_report_enabled}
                            onCheckedChange={(v) => updatePreference('weekly_report_enabled', v)}
                        />
                    </div>
                    
                    {preferences.weekly_report_enabled && (
                        <div className="flex gap-4 ml-8">
                            <div className="space-y-1">
                                <Label className="text-xs">{t.day}</Label>
                                <Select
                                    value={preferences.weekly_report_day}
                                    onValueChange={(v) => updatePreference('weekly_report_day', v)}
                                >
                                    <SelectTrigger className="w-32">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="sunday">{t.sunday}</SelectItem>
                                        <SelectItem value="monday">{t.monday}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">{t.time}</Label>
                                <Select
                                    value={preferences.weekly_report_time}
                                    onValueChange={(v) => updatePreference('weekly_report_time', v)}
                                >
                                    <SelectTrigger className="w-24">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="09:00">09:00</SelectItem>
                                        <SelectItem value="12:00">12:00</SelectItem>
                                        <SelectItem value="18:00">18:00</SelectItem>
                                        <SelectItem value="19:00">19:00</SelectItem>
                                        <SelectItem value="20:00">20:00</SelectItem>
                                        <SelectItem value="21:00">21:00</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}
                </div>

                {/* Challenge Reminders */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                    <div className="flex items-center gap-3">
                        <Trophy className="w-5 h-5 text-amber-500" />
                        <div>
                            <Label className="font-medium">{t.challengeReminders}</Label>
                            <p className="text-xs text-muted-foreground">{t.challengeRemindersDesc}</p>
                        </div>
                    </div>
                    <Switch
                        checked={preferences.challenge_reminders}
                        onCheckedChange={(v) => updatePreference('challenge_reminders', v)}
                    />
                </div>

                {/* Mood Reminders */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-pink-50 dark:bg-pink-950/30 border border-pink-200 dark:border-pink-800">
                    <div className="flex items-center gap-3">
                        <Smile className="w-5 h-5 text-pink-500" />
                        <div>
                            <Label className="font-medium">{t.moodReminders}</Label>
                            <p className="text-xs text-muted-foreground">{t.moodRemindersDesc}</p>
                        </div>
                    </div>
                    <Switch
                        checked={preferences.mood_reminders}
                        onCheckedChange={(v) => updatePreference('mood_reminders', v)}
                    />
                </div>

                {/* Morning Motivation */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800">
                    <div className="flex items-center gap-3">
                        <Coffee className="w-5 h-5 text-yellow-600" />
                        <div>
                            <Label className="font-medium">{t.morningMotivation}</Label>
                            <p className="text-xs text-muted-foreground">{t.morningMotivationDesc}</p>
                        </div>
                    </div>
                    <Switch
                        checked={preferences.morning_motivation}
                        onCheckedChange={(v) => updatePreference('morning_motivation', v)}
                    />
                </div>

                {/* === Auto-Push Cron Jobs (v3.18) === */}
                <div className="pt-2 border-t border-border">
                    <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-2">
                        {language === 'de' ? 'Automatische Push-Benachrichtigungen' : 'Automated push notifications'}
                    </p>
                </div>

                {/* Backup Reminder */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800" data-testid="pref-backup-reminder">
                    <div className="flex items-center gap-3">
                        <FolderDown className="w-5 h-5 text-indigo-500" />
                        <div>
                            <Label className="font-medium">
                                {language === 'de' ? 'Backup-Reminder (So 19:00)' : 'Backup reminder (Sun 19:00)'}
                            </Label>
                            <p className="text-xs text-muted-foreground">
                                {language === 'de' ? 'Wöchentlich falls Backup älter als 7 Tage' : 'Weekly if backup older than 7 days'}
                            </p>
                        </div>
                    </div>
                    <Switch
                        checked={preferences.backup_reminder_enabled}
                        onCheckedChange={(v) => updatePreference('backup_reminder_enabled', v)}
                    />
                </div>

                {/* Morning Routine Hint */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800" data-testid="pref-morning-hint">
                    <div className="flex items-center gap-3">
                        <Sunrise className="w-5 h-5 text-orange-500" />
                        <div>
                            <Label className="font-medium">
                                {language === 'de' ? 'Morgenroutine-Hint (täglich 07:30)' : 'Morning routine hint (daily 07:30)'}
                            </Label>
                            <p className="text-xs text-muted-foreground">
                                {language === 'de' ? 'Nur wenn heute noch kein Auto-Schlaf-Eintrag' : 'Only if no auto-sleep entry today'}
                            </p>
                        </div>
                    </div>
                    <Switch
                        checked={preferences.morning_routine_hint_enabled}
                        onCheckedChange={(v) => updatePreference('morning_routine_hint_enabled', v)}
                    />
                </div>

                {/* Weekly Briefing Push */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-teal-50 dark:bg-teal-950/30 border border-teal-200 dark:border-teal-800" data-testid="pref-weekly-briefing">
                    <div className="flex items-center gap-3">
                        <Sparkles className="w-5 h-5 text-teal-500" />
                        <div>
                            <Label className="font-medium">
                                {language === 'de' ? 'Wochen-Briefing Push (Mo 07:00)' : 'Weekly briefing push (Mon 07:00)'}
                            </Label>
                            <p className="text-xs text-muted-foreground">
                                {language === 'de' ? 'Nanouks Wochen-Statement zum Start' : "Nanouk's weekly statement at the start"}
                            </p>
                        </div>
                    </div>
                    <Switch
                        checked={preferences.weekly_briefing_push_enabled}
                        onCheckedChange={(v) => updatePreference('weekly_briefing_push_enabled', v)}
                    />
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800" data-testid="pref-symptom-prediction">
                    <div className="flex items-center gap-3">
                        <Sparkles className="w-5 h-5 text-amber-500" />
                        <div>
                            <Label className="font-medium">
                                {language === 'de' ? 'Symptom-Vorhersage (täglich 08:00)' : 'Symptom prediction (daily 08:00)'}
                            </Label>
                            <p className="text-xs text-muted-foreground">
                                {language === 'de' ? 'Nanouk warnt morgens vor erhöhtem Symptom-Risiko' : 'Nanouk warns of elevated symptom risk in the morning'}
                            </p>
                        </div>
                    </div>
                    <Switch
                        checked={preferences.symptom_prediction_enabled !== false}
                        onCheckedChange={(v) => updatePreference('symptom_prediction_enabled', v)}
                    />
                </div>

                {/* Save Button */}
                <Button onClick={handleSave} disabled={saving} className="w-full">
                    <Save className="w-4 h-4 mr-2" />
                    {t.save}
                </Button>
            </CardContent>
        </Card>
    );
}
